import React, { FC, useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Text,
  VStack,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Button,
  IconButton,
  Spinner,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Flex,
  Spacer,
  Tooltip,
  Grid,
  GridItem,
  Img,
  Link,
  Select,
} from '@chakra-ui/react'
import { FiSearch, FiChevronLeft, FiChevronRight } from 'react-icons/fi'
import {
  useGetAdminUsersQuery,
  useUpdateUserPremiumStatusMutation,
} from '../../../redux/services/skeema/users.endpoints'
import SubNavButtons from '../../../pages/morningPageV3/SubNavButtons'
import { APP_LOGO_WORDMARK_URL, APP_NAME } from '../../../constants'
import { setUserOverride } from '../../../redux/userSlice'
import { useDispatch } from 'react-redux'
import CopyableText from '../../../components/CopyableText'
import EmptyState from '../../../components/EmptyState'
import FeatureFlagsModal from '../../../components/FeatureFlagsModal'

const AdminPage: FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [tierFilter, setTierFilter] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [isFeatureFlagsModalOpen, setIsFeatureFlagsModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<{
    email: string
    featureFlags: Record<string, boolean>
  } | null>(null)
  const pageSize = 200
  const searchInputRef = useRef<HTMLInputElement>(null)
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Clear user override when navigating to admin page
  useEffect(() => {
    dispatch(setUserOverride(null))
  }, [dispatch])

  // Auto-focus search input when component mounts
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [])

  // Debounced search effect
  useEffect(() => {
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }

    // Set new timeout to update search term after 300ms
    debounceTimeoutRef.current = setTimeout(() => {
      setSearchTerm(searchInput)
      setCurrentPage(1) // Reset to first page when searching
    }, 300)

    // Cleanup timeout on unmount
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [searchInput])

  const { data, error, isLoading, refetch } = useGetAdminUsersQuery({
    search: searchTerm,
    tier: tierFilter,
    page: currentPage,
    page_size: pageSize,
  })

  const [updateUserPremiumStatus, { isLoading: isUpdatingPremium }] =
    useUpdateUserPremiumStatusMutation()

  const handleSearch = (value: string) => {
    setSearchInput(value)
  }

  const handleTierFilter = (value: string) => {
    setTierFilter(value)
    setCurrentPage(1) // Reset to first page when filtering
  }

  const handleClearFilters = () => {
    setSearchInput('')
    setSearchTerm('')
    setTierFilter('')
    setCurrentPage(1)
  }

  const handleUpgrade = async (email: string) => {
    try {
      await updateUserPremiumStatus({ email, is_premium: true }).unwrap()
      refetch() // Refresh the user list
    } catch (error) {
      console.error('Failed to upgrade user:', error)
    }
  }

  const handleDowngrade = async (email: string) => {
    try {
      await updateUserPremiumStatus({ email, is_premium: false }).unwrap()
      refetch() // Refresh the user list
    } catch (error) {
      console.error('Failed to downgrade user:', error)
    }
  }

  const handleLoginAs = (email: string) => {
    navigate(`/?__user_override=${email}`)
  }

  const handleViewFeatureFlags = (email: string, userFeatureFlags: Record<string, boolean>) => {
    setSelectedUser({ email, featureFlags: userFeatureFlags })
    setIsFeatureFlagsModalOpen(true)
  }

  const handleCloseFeatureFlagsModal = () => {
    setIsFeatureFlagsModalOpen(false)
    setSelectedUser(null)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  const getTierBadgeColor = (tier: string) => {
    return tier === 'Pro' ? 'green' : 'gray'
  }

  if (error) {
    return (
      <Box p={8}>
        <Alert status="error">
          <AlertIcon />
          Failed to load users. Please try again.
        </Alert>
        <Button mt={4} onClick={() => refetch()}>
          Retry
        </Button>
      </Box>
    )
  }

  return (
    <Flex direction="column" h="100%" w="100%">
      {/* Main Content Area */}
      <Grid
        templateColumns="repeat(12, 1fr)"
        templateRows="68px auto"
        flex="1 1 auto"
        p="13px 24px 0 24px"
      >
        {/* Header Row */}
        <GridItem colSpan={8} gridRow={1} minWidth="552px" maxWidth="1000px">
          <Flex alignItems="center" h="100%" justifyContent="flex-start">
            <Link href="/" onClick={() => navigate('/')}>
              <Img src={APP_LOGO_WORDMARK_URL} alt={`${APP_NAME} logo`} height="22px" />
            </Link>
          </Flex>
        </GridItem>

        <GridItem colSpan={4} gridColumnStart={9} gridRow={1} minWidth="154px">
          <Flex alignItems="center" h="100%" justifyContent="flex-end">
            <SubNavButtons />
          </Flex>
        </GridItem>

        {/* Content Area */}
        <GridItem colSpan={12} gridRow={2} overflow="auto" borderRadius="16px">
          <Box backgroundColor="#f6f6f6" borderRadius="16px" p={6} minHeight="100%">
            <VStack spacing={6} align="stretch">
              <Text fontSize="2xl" fontWeight="bold" color="#585858">
                Admin View - User Management
              </Text>

              {/* Search and Filter Controls */}
              <HStack spacing={4} align="stretch">
                <InputGroup maxW="400px">
                  <InputLeftElement pointerEvents="none">
                    <FiSearch color="gray.300" />
                  </InputLeftElement>
                  <Input
                    ref={searchInputRef}
                    placeholder="Search by email..."
                    value={searchInput}
                    onChange={(e) => handleSearch(e.target.value)}
                    bg="white"
                    border="1px solid #E2E8F0"
                    _focus={{
                      borderColor: '#3182CE',
                      boxShadow: '0 0 0 1px #3182CE',
                    }}
                  />
                </InputGroup>

                <Select
                  placeholder="All tiers"
                  value={tierFilter}
                  onChange={(e) => handleTierFilter(e.target.value)}
                  bg="white"
                  border="1px solid #E2E8F0"
                  maxW="200px"
                  _focus={{
                    borderColor: '#3182CE',
                    boxShadow: '0 0 0 1px #3182CE',
                  }}
                >
                  <option value="basic">Basic</option>
                  <option value="pro">Pro</option>
                </Select>

                {(searchInput || tierFilter) && (
                  <Button
                    variant="outline"
                    height="40px"
                    onClick={handleClearFilters}
                    color="#3182CE"
                    borderColor="#3182CE"
                    bg="white"
                    fontSize="sm"
                    fontWeight="normal"
                    px={4}
                    _hover={{
                      bg: '#3182CE',
                      color: 'white',
                    }}
                  >
                    Clear filters
                  </Button>
                )}
              </HStack>

              {/* Users Table */}
              {isLoading ? (
                <Flex justify="center" py={8}>
                  <Spinner size="lg" color="blue.500" />
                </Flex>
              ) : data && data.results.length > 0 ? (
                <TableContainer bg="white" borderRadius="12px" boxShadow="sm">
                  <Table variant="striped" colorScheme="gray">
                    <Thead>
                      <Tr>
                        <Th color="#585858" fontWeight="600">
                          Email
                        </Th>
                        <Th color="#585858" fontWeight="600">
                          Sign Up Date
                        </Th>
                        <Th color="#585858" fontWeight="600">
                          Tier
                        </Th>
                        <Th color="#585858" fontWeight="600">
                          Has Bootstrapped
                        </Th>
                        <Th color="#585858" fontWeight="600">
                          Actions
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {data.results.map((user) => (
                        <Tr key={user.pk}>
                          <Td>
                            <CopyableText text={user.email} />
                          </Td>
                          <Td color="#585858">{formatDate(user.signup_date)}</Td>
                          <Td>
                            <Badge colorScheme={getTierBadgeColor(user.tier)}>{user.tier}</Badge>
                          </Td>
                          <Td>
                            <Badge colorScheme={user.has_bootstrapped ? 'green' : 'red'}>
                              {user.has_bootstrapped ? 'Yes' : 'No'}
                            </Badge>
                          </Td>
                          <Td>
                            <HStack spacing={2}>
                              {!user.is_premium && (
                                <Tooltip label="Upgrade to Pro">
                                  <Button
                                    size="sm"
                                    colorScheme="green"
                                    onClick={() => handleUpgrade(user.email)}
                                    isLoading={isUpdatingPremium}
                                    loadingText="Upgrading..."
                                  >
                                    Upgrade
                                  </Button>
                                </Tooltip>
                              )}
                              {user.is_premium && (
                                <Tooltip label="Downgrade to Basic">
                                  <Button
                                    size="sm"
                                    colorScheme="orange"
                                    onClick={() => handleDowngrade(user.email)}
                                    isLoading={isUpdatingPremium}
                                    loadingText="Downgrading..."
                                  >
                                    Downgrade
                                  </Button>
                                </Tooltip>
                              )}
                              <Tooltip label="Login as this user">
                                <Button
                                  size="sm"
                                  colorScheme="blue"
                                  onClick={() => handleLoginAs(user.email)}
                                >
                                  Login as
                                </Button>
                              </Tooltip>
                              <Tooltip label="View feature flags">
                                <Button
                                  size="sm"
                                  colorScheme="purple"
                                  onClick={() =>
                                    handleViewFeatureFlags(user.email, user.feature_flags)
                                  }
                                >
                                  Feature Flags
                                </Button>
                              </Tooltip>
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              ) : (
                <EmptyState
                  hasSearchTerm={!!searchInput}
                  hasTierFilter={!!tierFilter}
                  onClearFilters={handleClearFilters}
                />
              )}

              {/* Pagination */}
              {data && data.results.length > 0 && (
                <Flex align="center" bg="white" p={4} borderRadius="12px" boxShadow="sm">
                  <Text fontSize="sm" color="#A7A7A7">
                    Showing {data.results.length} of {data.count} users
                  </Text>
                  <Spacer />
                  <HStack spacing={2}>
                    <IconButton
                      aria-label="Previous page"
                      icon={<FiChevronLeft />}
                      size="sm"
                      isDisabled={!data.previous}
                      onClick={() => setCurrentPage(currentPage - 1)}
                      variant="outline"
                    />
                    <Text fontSize="sm" color="#585858">
                      Page {currentPage}
                    </Text>
                    <IconButton
                      aria-label="Next page"
                      icon={<FiChevronRight />}
                      size="sm"
                      isDisabled={!data.next}
                      onClick={() => setCurrentPage(currentPage + 1)}
                      variant="outline"
                    />
                  </HStack>
                </Flex>
              )}
            </VStack>
          </Box>
        </GridItem>
      </Grid>

      {/* Feature Flags Modal */}
      {selectedUser && (
        <FeatureFlagsModal
          isOpen={isFeatureFlagsModalOpen}
          onClose={handleCloseFeatureFlagsModal}
          userEmail={selectedUser.email}
          featureFlags={selectedUser.featureFlags}
        />
      )}
    </Flex>
  )
}

export default React.memo(AdminPage)
